import { Mail, Phone, MapPin } from "lucide-react";
import { useLocation } from "react-router-dom";
import { FOOTER } from "@/constants/testIds/landing";
import TrackedPhoneLink from "@/components/site/TrackedPhoneLink";
import { COURSE_LIST } from "@/data/courses";
import { FEATURED_COURSE_IDS } from "@/data/featuredCourses";

export default function Footer() {
  const { pathname } = useLocation();
  const popularCourses = FEATURED_COURSE_IDS
    .map((id) => COURSE_LIST.find((c) => c.id === id))
    .filter(Boolean);

  // Förder-Satz kursabhängig: Direktbuchungs-Kurse (z. B. Wundexperte, PDL) haben keinen Bildungsgutschein
  const slugMatch = pathname.match(/^\/kurse\/([^/]+)/);
  const currentCourse = slugMatch ? COURSE_LIST.find((c) => c.id === slugMatch[1]) : null;
  const fundingSentence = currentCourse && !/bildungsgutschein/i.test(currentCourse.funding || "")
    ? "Direktbuchung oder Ratenzahlung (3, 6 oder 12 Raten, ohne Aufschlag)."
    : "Förderung über Bildungsgutschein möglich.";

  return (
    <footer className="relative bg-espresso-deep text-cream-100 overflow-hidden">
      {/* Riesiges Gothic-Wordmark — Kinetic-Endcard-Signatur */}
      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-16">
        <div className="flex items-center justify-between text-[10px] uppercase tracking-[0.32em] font-bold text-cream-100/70 mb-8">
          <span>Akademie für Pflege · Ausgabe 2026</span>
          <span className="text-saffron">Abspann</span>
        </div>
        <div className="font-magazine font-medium leading-[1.08] text-cream-50 text-[3.4rem] sm:text-[5.5rem] lg:text-[7.5rem] select-none border-b border-cream-100/15 pb-10">
          Dickies-<span className="italic text-saffron">Campus.</span>
        </div>
      </div>

      {/* Vertical text · running up the right edge */}
      <div
        aria-hidden="true"
        className="hidden lg:block absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none"
        style={{ writingMode: "vertical-rl", transform: "rotate(180deg) translateX(50%)" }}
      >
        <span className="text-[10px] uppercase tracking-[0.34em] text-cream-100/60 font-semibold whitespace-nowrap">
          Dickies-Campus — Bildung für die Pflege · Edition 2026
        </span>
      </div>

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-14">
        <div className="grid md:grid-cols-2 lg:grid-cols-5 gap-10">
          <div className="lg:col-span-1">
            <img src="/dh_logo_terra.png" alt="Dickies-Campus Logo" className="h-14 w-14 object-contain mb-5 brightness-0 invert opacity-90" />
            <p className="text-cream-100/70 text-sm max-w-md leading-relaxed">
              AZAV zertifizierte Bildungseinrichtung für die Pflege. Online-Weiterbildungen, jeden Montag startklar.{" "}
              {fundingSentence}
            </p>
          </div>

          <div>
            <div className="text-xs uppercase tracking-widest text-saffron font-bold mb-4">Beliebte Kurse</div>
            <ul className="space-y-2.5 text-cream-100/75 text-sm">
              {popularCourses.map((c) => (
                <li key={c.id}>
                  <a
                    href={`/kurse/${c.id}`}
                    data-testid={`footer-link-course-${c.id}`}
                    className="hover:text-saffron"
                  >
                    {c.title}
                  </a>
                </li>
              ))}
              <li>
                <a
                  href="/#kurse"
                  data-testid="footer-link-courses-all"
                  className="text-saffron font-semibold hover:text-cream-50"
                >
                  Alle Kurse ansehen →
                </a>
              </li>
            </ul>
          </div>

          <div>
            <div className="text-xs uppercase tracking-widest text-saffron font-bold mb-4">Navigation</div>
            <ul className="space-y-2.5 text-cream-100/75 text-sm">
              <li><a href="/pflegekraefte" className="hover:text-saffron">Für Pflegekräfte</a></li>
              <li><a href="/pflegedienste" className="hover:text-saffron">Für Einrichtungen</a></li>
              <li><a href="/pflichtfortbildungen" data-testid="footer-link-pflicht" className="hover:text-saffron">Pflichtfortbildungen</a></li>
              <li><a href="/campus" className="hover:text-saffron">Dickies-Campus</a></li>
              <li><a href="/preise" className="hover:text-saffron">Preise</a></li>
              <li><a href="/#faq" className="hover:text-saffron">FAQ</a></li>
              <li><a href="/#kontakt" data-testid={FOOTER.kontakt} className="hover:text-saffron">Kontakt</a></li>
            </ul>
          </div>

          <div>
            <div className="text-xs uppercase tracking-widest text-saffron font-bold mb-4">Kostenlose Leitfäden</div>
            <ul className="space-y-2.5 text-cream-100/75 text-sm">
              <li>
                <a href="/leitfaden/angehoerigen-beschwerde" data-testid="footer-link-leitfaden-angehoerigen" className="hover:text-saffron">
                  Angehörigen-Beschwerde · 5-Min-Leitfaden
                </a>
              </li>
              <li>
                <a href="/leitfaden/sturz-bewohner" data-testid="footer-link-leitfaden-sturz" className="hover:text-saffron">
                  Sturz eines Bewohners · Akutleitfaden
                </a>
              </li>
              <li>
                <a href="/leitfaden/demenz-25-saetze" data-testid="footer-link-leitfaden-demenz" className="hover:text-saffron">
                  Demenz · 25 Sätze, die wirklich helfen
                </a>
              </li>
              <li>
                <a href="/leitfaden/md-pruefung-7-tage" data-testid="footer-link-leitfaden-md" className="hover:text-saffron">
                  MD-Prüfung in 7 Tagen vorbereiten
                </a>
              </li>
              <li>
                <a href="/leitfaeden" data-testid="footer-link-leitfaeden-alle" className="text-saffron font-semibold hover:text-cream-50">
                  Alle Leitfäden ansehen →
                </a>
              </li>
              <li>
                <a href="/blog" data-testid="footer-link-blog" className="hover:text-saffron">
                  Blog · Wissen & Weiterbildung
                </a>
              </li>
              <li>
                <a href="/campus" className="hover:text-saffron">
                  Helden-Schatzkiste (Mitglieder)
                </a>
              </li>
              <li>
                <a href="/shop/richtig-abrechnen" className="hover:text-saffron">
                  Pflegeshop · eBooks
                </a>
              </li>
            </ul>
          </div>

          <div>
            <div className="text-xs uppercase tracking-widest text-saffron font-bold mb-4">Kontakt</div>
            <ul className="space-y-2.5 text-cream-100/75 text-sm">
              <li className="flex items-center gap-2"><Phone className="h-4 w-4" strokeWidth={1.5}/> <TrackedPhoneLink source="footer" data-testid="footer-phone-cta" className="hover:text-saffron">0211 29273330</TrackedPhoneLink></li>
              <li className="flex items-center gap-2"><Mail className="h-4 w-4" strokeWidth={1.5}/> <a href="mailto:info@dickies-helden.de" className="hover:text-saffron">info@dickies-helden.de</a></li>
              <li className="flex items-start gap-2"><MapPin className="h-4 w-4 mt-0.5 shrink-0" strokeWidth={1.5}/> Dickies-Campus GmbH<br />Neuenhausplatz 73<br />40699 Erkrath</li>
            </ul>
          </div>
        </div>

        <div className="mt-14 pt-6 border-t border-cream-100/15 flex flex-col md:flex-row md:items-center md:justify-between gap-3 text-xs text-cream-100/75">
          <div className="flex flex-wrap items-center gap-x-6 gap-y-2">
            <span>© {new Date().getFullYear()} Dickies-Campus GmbH</span>
            <span className="hidden md:inline text-cream-100/60">·</span>
            <span className="text-[11px] text-cream-100/70">
              Website &amp; Technik:{" "}
              <a href="https://joudi-group.com" target="_blank" rel="noopener noreferrer" data-testid="footer-link-joudi" className="underline underline-offset-2 hover:text-saffron">Joudi Group</a>
            </span>
          </div>
          <div className="flex gap-5 flex-wrap">
            <a href="/azav-zulassung" data-testid="footer-link-azav" className="hover:text-saffron">
              AZAV-zugelassener Träger · Reg.-Nr. 0184.1-10141-AZAV-T
            </a>
            <a href="/leitbild" data-testid="footer-link-leitbild" className="hover:text-saffron">Unser Leitbild</a>
            <a href="/services" data-testid="footer-link-services" className="hover:text-saffron">Services für Pflegedienste</a>
            <a href="/impressum" data-testid={FOOTER.impressum} className="hover:text-saffron">Impressum</a>
            <a href="/datenschutz" data-testid={FOOTER.datenschutz} className="hover:text-saffron">Datenschutz</a>
            <a href="/barrierefreiheit" data-testid="footer-barrierefreiheit" className="hover:text-saffron">Barrierefreiheit</a>
            <a href="/agb" data-testid="footer-link-agb" className="hover:text-saffron">AGB</a>
          </div>
        </div>
      </div>
    </footer>
  );
}
